import { useAnimationSettings } from "./animation";

// ============================================
// Animation Constants (Centralized)
// ============================================

/**
 * Duration constants for consistent animation timing
 */
export const DURATION = {
	instant: 0.01,
	fast: 0.2,
	normal: 0.4,
	slow: 0.6,
	editorial: 0.8,
} as const;

/**
 * Delay constants for orchestrated animations
 */
export const DELAY = {
	none: 0,
	short: 0.1,
	medium: 0.2,
	long: 0.3,
	editorial: 0.5,
} as const;

/**
 * Stagger configurations for list animations
 */
export const STAGGER = {
	tight: { base: 0.1, increment: 0.03 },
	normal: { base: 0.2, increment: 0.05 },
	relaxed: { base: 0.2, increment: 0.1 },
	editorial: { base: 0.2, increment: 0.15 },
} as const;

/**
 * Counter animation duration in milliseconds
 * Used for stats counters and numeric animations
 */
export const COUNTER_DURATION_MS = 2000;

/**
 * Custom easing curves for luxury/editorial feel
 */
export const EASING = {
	smooth: [0.22, 1, 0.36, 1] as const, // Slow start, smooth land
	bounce: [0.68, -0.55, 0.265, 1.55] as const,
	snappy: [0.25, 0.1, 0.25, 1] as const,
} as const;

// ============================================
// Motion Utilities
// ============================================

/**
 * Motion utilities with accessibility support (prefers-reduced-motion)
 * AND CMS control (enableAnimations feature flag)
 *
 * WCAG 2.1 Success Criterion 2.3.3 requires providing alternatives
 * for users who prefer reduced motion.
 *
 * Animations are disabled when EITHER:
 * - CMS admin disables animations
 * - User has prefers-reduced-motion enabled
 */

/**
 * Hook that returns animation-safe values based on:
 * 1. CMS enableAnimations flag
 * 2. User's motion preference (prefers-reduced-motion)
 *
 * When either condition requires reduction, animations are disabled/minimized.
 */
export function useSafeMotion() {
	const { shouldReduceMotion: shouldReduce } = useAnimationSettings();

	return {
		shouldReduce,

		// Fade animations - always safe, just adjust timing
		fadeIn: {
			initial: { opacity: 0 },
			animate: { opacity: 1 },
			transition: { duration: shouldReduce ? 0.01 : 0.5 },
		},

		// Slide animations - disable movement when reduced motion preferred
		slideUp: {
			initial: { opacity: 0, y: shouldReduce ? 0 : 20 },
			animate: { opacity: 1, y: 0 },
			transition: { duration: shouldReduce ? 0.01 : 0.5 },
		},

		slideDown: {
			initial: { opacity: 0, y: shouldReduce ? 0 : -20 },
			animate: { opacity: 1, y: 0 },
			transition: { duration: shouldReduce ? 0.01 : 0.5 },
		},

		slideLeft: {
			initial: { opacity: 0, x: shouldReduce ? 0 : 50 },
			animate: { opacity: 1, x: 0 },
			transition: {
				duration: shouldReduce ? 0.01 : 0.6,
				ease: "easeOut" as const,
			},
		},

		slideRight: {
			initial: { opacity: 0, x: shouldReduce ? 0 : -50 },
			animate: { opacity: 1, x: 0 },
			transition: {
				duration: shouldReduce ? 0.01 : 0.6,
				ease: "easeOut" as const,
			},
		},

		// Scale animations
		scaleIn: {
			initial: { opacity: 0, scale: shouldReduce ? 1 : 0.8 },
			animate: { opacity: 1, scale: 1 },
			transition: { duration: shouldReduce ? 0.01 : 0.3 },
		},

		// Hover/tap animations - disable completely when reduced
		hoverScale: shouldReduce ? {} : { scale: 1.05 },
		hoverScaleLarge: shouldReduce ? {} : { scale: 1.1 },
		hoverScaleSmall: shouldReduce ? {} : { scale: 1.02 },
		tapScale: shouldReduce ? {} : { scale: 0.95 },

		// Viewport animation settings
		viewportOnce: { once: true, margin: "-100px" as const },
	};
}

/**
 * Creates a staggered delay for list items
 * @param index - Item index in the list
 * @param baseDelay - Base delay before stagger starts
 * @param staggerAmount - Delay increment per item
 */
export function getStaggerDelay(
	index: number,
	baseDelay = 0.2,
	staggerAmount = 0.05,
	shouldReduce = false,
): number {
	return shouldReduce ? 0 : baseDelay + index * staggerAmount;
}

/**
 * Slide variants for AnimatePresence (e.g., image galleries)
 * Returns minimal variants when reduced motion is preferred
 */
export function getSlideVariants(shouldReduce: boolean) {
	if (shouldReduce) {
		return {
			enter: { opacity: 0 },
			center: { opacity: 1, zIndex: 1 },
			exit: { opacity: 0, zIndex: 0 },
		};
	}

	return {
		enter: (direction: number) => ({
			x: direction > 0 ? 300 : -300,
			opacity: 0,
		}),
		center: {
			zIndex: 1,
			x: 0,
			opacity: 1,
		},
		exit: (direction: number) => ({
			zIndex: 0,
			x: direction < 0 ? 300 : -300,
			opacity: 0,
		}),
	};
}

/**
 * Spring transition config - used for smoother animations
 * Returns instant transition when reduced motion preferred
 */
export function getSpringTransition(shouldReduce: boolean) {
	if (shouldReduce) {
		return { duration: 0.01 };
	}

	return {
		x: { type: "spring" as const, stiffness: 300, damping: 30 },
		opacity: { duration: 0.2 },
	};
}

// ============================================
// Editorial/Luxury Animation Variants
// ============================================

/**
 * Orchestrated page entrance animation with staggered reveals
 * Creates a refined, editorial feel as content enters the viewport
 *
 * Usage with Framer Motion:
 * <motion.div variants={pageEntrance.container} initial="initial" animate="animate">
 *   <motion.div variants={pageEntrance.item(shouldReduce)}>...</motion.div>
 * </motion.div>
 */
export const pageEntrance = {
	container: {
		initial: {},
		animate: {
			transition: {
				staggerChildren: 0.15,
				delayChildren: 0.1,
			},
		},
	},
	/**
	 * Item variant - respects reduced motion preference
	 */
	item: (shouldReduce: boolean) => ({
		initial: { opacity: 0, y: shouldReduce ? 0 : 40 },
		animate: {
			opacity: 1,
			y: 0,
			transition: {
				duration: shouldReduce ? 0.01 : 0.7,
				ease: [0.22, 1, 0.36, 1], // Custom easing - slow start, smooth land
			},
		},
	}),
};

/**
 * Image reveal animation - graceful scale with fade
 * Used for project images and hero visuals
 */
export function getImageReveal(shouldReduce: boolean) {
	return {
		initial: { scale: shouldReduce ? 1 : 1.1, opacity: 0 },
		animate: {
			scale: 1,
			opacity: 1,
			transition: {
				duration: shouldReduce ? 0.01 : 0.8,
				ease: [0.22, 1, 0.36, 1],
			},
		},
	};
}

/**
 * Image hover animation - subtle zoom with brightness
 * Not jarring scale jump, but refined movement
 */
export function getImageHover(shouldReduce: boolean) {
	if (shouldReduce) {
		return { rest: {}, hover: {} };
	}

	return {
		rest: { scale: 1, filter: "brightness(1)" },
		hover: {
			scale: 1.02,
			filter: "brightness(1.02)",
			transition: { duration: 0.5, ease: "easeOut" },
		},
	};
}

/**
 * Gold accent line animation
 * Decorative element that reveals from center
 */
export function getGoldLineReveal(shouldReduce: boolean) {
	return {
		initial: { scaleX: 0, opacity: 0 },
		animate: {
			scaleX: 1,
			opacity: 1,
			transition: {
				duration: shouldReduce ? 0.01 : 0.8,
				ease: [0.22, 1, 0.36, 1],
				delay: shouldReduce ? 0 : 0.3,
			},
		},
	};
}

/**
 * Card hover elevation - subtle lift with shadow
 * For project cards and service cards
 */
export function getCardHover(shouldReduce: boolean) {
	if (shouldReduce) {
		return { rest: {}, hover: {} };
	}

	return {
		rest: {
			y: 0,
			boxShadow: "0 4px 20px oklch(0.25 0.02 75 / 8%)",
		},
		hover: {
			y: -4,
			boxShadow: "0 12px 40px oklch(0.2 0.02 75 / 15%)",
			transition: { duration: 0.3, ease: "easeOut" },
		},
	};
}

/**
 * Text reveal animation - for headlines and key copy
 * Slides up with slight blur fade
 */
export function getTextReveal(shouldReduce: boolean) {
	return {
		initial: {
			opacity: 0,
			y: shouldReduce ? 0 : 20,
			filter: shouldReduce ? "none" : "blur(4px)",
		},
		animate: {
			opacity: 1,
			y: 0,
			filter: "blur(0px)",
			transition: {
				duration: shouldReduce ? 0.01 : 0.6,
				ease: [0.22, 1, 0.36, 1],
			},
		},
	};
}

/**
 * Overlay reveal animation - for hover overlays on cards
 * Fades in smoothly on hover
 */
export function getOverlayReveal(shouldReduce: boolean) {
	if (shouldReduce) {
		return { rest: {}, hover: {} };
	}

	return {
		rest: { opacity: 0 },
		hover: {
			opacity: 1,
			transition: { duration: 0.3 },
		},
	};
}

/**
 * Card hover with image scale - for project cards
 * Lifts card and scales inner image on hover
 */
export function getProjectCardHover(shouldReduce: boolean) {
	if (shouldReduce) {
		return {
			card: { rest: {}, hover: {} },
			image: { rest: {}, hover: {} },
			overlay: { rest: {}, hover: {} },
			content: { rest: {}, hover: {} },
		};
	}

	return {
		card: {
			rest: { y: 0, scale: 1 },
			hover: {
				y: -8,
				scale: 1.02,
				transition: { duration: 0.3, ease: "easeOut" as const },
			},
		},
		image: {
			rest: { scale: 1 },
			hover: {
				scale: 1.1,
				transition: { duration: 0.6, ease: "easeOut" as const },
			},
		},
		overlay: {
			rest: { opacity: 0 },
			hover: {
				opacity: 1,
				transition: { duration: 0.3 },
			},
		},
		content: {
			rest: { y: 0, opacity: 1 },
			hover: {
				y: -4,
				opacity: 1,
				transition: { duration: 0.3, ease: "easeOut" as const },
			},
		},
	};
}
